import { store } from "@/store/store";
import Cookies from "js-cookie";

// Initialize dataLayer (GTM creates this automatically, but ensure it exists)
const initDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

// Push to GTM dataLayer
const gtmPush = (data) => {
  initDataLayer();
  window.dataLayer.push(data);
};

const DEFAULT_PREFERENCES = {
  _necessary: true,
  _performance: false,
  _marketing: false,
};

// Initialize consent based on saved preferences
export const initGA = () => {
  // Note: gtag('consent', 'default', ...) is already set in index.html before GTM loads.
  // This function pushes a custom dataLayer event for any GTM triggers that need it.

  const savedConsent = Cookies.get("cookieConsent");
  const savedPreferences = Cookies.get("cookiePreferences");

  if (!savedConsent) {
    // First time visitor - push event for GTM triggers
    gtmPush({
      event: "cookie_consent",
      ad_storage: "denied",
      security_storage: "granted",
      analytics_storage: "denied",
      functionality_storage: "denied",
      personalization_storage: "denied",
      ad_personalization: "denied",
      ad_user_data: "denied",
    });
  } else {
    // Returning user - restore their preferences
    let preferences;
    try {
      preferences = savedPreferences
        ? JSON.parse(savedPreferences)
        : DEFAULT_PREFERENCES;
    } catch {
      preferences = DEFAULT_PREFERENCES;
    }

    const consentState = {
      ad_storage: preferences._marketing ? "granted" : "denied",
      analytics_storage: preferences._performance ? "granted" : "denied",
      functionality_storage: "granted",
      personalization_storage: preferences._marketing ? "granted" : "denied",
      ad_personalization: preferences._marketing ? "granted" : "denied",
      ad_user_data: preferences._marketing ? "granted" : "denied",
    };

    // Also call consent update as a safety net in case index.html defaults
    // didn't read the cookie correctly (e.g., encoding differences with js-cookie)
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag("consent", "update", consentState);

    gtmPush({
      event: "cookie_consent",
      security_storage: "granted",
      ...consentState,
    });
  }
};

export const getEventCategory = () => {
  const title = document.title;

  if (title.includes("Home Auctions")) return "NEW-SRP";
  if (title.includes("Homes For Sale")) return "Home Page 2.0";
  if (title.includes("About-us")) return "about-us";

  // fallback
  return "Home Page 2.0";
};

// Cache for performance optimization
let cachedUserContext = null;
let cachedPageInfo = null;

// Get user context helper with caching
const getUserContext = () => {
  // Always re-read visitorId from cookie since it may be set asynchronously
  // by the useVisitorId hook after the initial cache was created.
  const visitorId = Cookies.get("visitorId") || "";

  // Resolve the user id from the store, falling back to the analytics user id
  // captured at registration time (stored in the `gaUserId` cookie). During
  // quick/temporary registration the BE only starts returning the id in the
  // store after the profile is completed, so the store id is empty in between.
  // The cookie bridges that window so `user_uid` is present on ALL events, not
  // just the registration events. Set on register, cleared on login/logout.
  const user = store.getState();
  const userId = user.user.user?.realbidUserId || Cookies.get("gaUserId") || "";

  if (!cachedUserContext) {
    cachedUserContext = {
      user_id: userId,
      user_uid: userId,
      visitor_id: visitorId,
      user_type: user.user.userType || "anonymous",
      is_logged_in: user.user.isUserLoggedIn || false,
    };
  } else {
    // Refresh values that can change after the cache was first built:
    //  - visitor_id may be set asynchronously after initial cache.
    //  - the user id can change when the id is (re)established, e.g. after a
    //    quick register followed by full profile completion with a new id.
    // Only overwrite the cached id when we have a resolved id AND it differs,
    // so we never clobber a real id with an empty one.
    cachedUserContext.visitor_id = visitorId;
    // Keep the id in sync with the resolved value. Assign directly (not guarded
    // by `userId &&`) so that when the id becomes empty — e.g. after logout —
    // the stale id is cleared instead of lingering.
    if (cachedUserContext.user_id !== userId) {
      cachedUserContext.user_id = userId;
      cachedUserContext.user_uid = userId;
    }
    // Keep user_type / is_logged_in in sync with the store as they change.
    cachedUserContext.user_type = user.user.userType || "anonymous";
    cachedUserContext.is_logged_in = user.user.isUserLoggedIn || false;
  }
  return cachedUserContext;
};

// Get page info with caching
const getPageInfo = () => {
  if (!cachedPageInfo) {
    cachedPageInfo = {
      page_title: document.title,
      page_location: window.location.href,
    };
  }
  return cachedPageInfo;
};

// Clear cache when needed
export const clearAnalyticsCache = () => {
  cachedUserContext = null;
  cachedPageInfo = null;
};

// Consent helper check function
const hasAnalyticsConsent = () => {
  try {
    const preferences = JSON.parse(Cookies.get("cookiePreferences") || "{}");
    return preferences._performance === true;
  } catch (error) {
    console.warn("Failed to parse cookie preferences:", error);
    return false;
  }
};

// List of consent-banner interaction events that must be tracked
// even when the user has NOT yet granted analytics consent.
const CONSENT_INTERACTION_EVENTS = new Set([
  "manage_cookies",
  "user_selected_manage_cookies",
  "manage_cookies_footer_click",
  "manage_cookie_consent_closed",
  "cookie_consent_accepted",
  "cookie_consent_accepted_only_essentials",
  "cookie_consent_accepted_user_selected",
  "main_cookie_consent_closed",
]);

// Add consent update function
export const updateGTMConsent = (
  consentSettings,
  { visitorId: overrideVisitorId } = {},
) => {
  const userContext = getUserContext();
  const eventName = consentSettings?.event_name || "cookie_consent";
  const resolvedVisitorId = overrideVisitorId || userContext.visitor_id;

  // Determine the real consent values the caller wants to persist
  const consentUpdate = {
    ad_storage: consentSettings?.ad_storage || "denied",
    analytics_storage: consentSettings?.analytics_storage || "denied",
    functionality_storage: consentSettings?.functionality_storage || "granted",
    personalization_storage:
      consentSettings?.personalization_storage || "denied",
    ad_personalization: consentSettings?.ad_personalization || "denied",
    ad_user_data: consentSettings?.ad_user_data || "denied",
  };

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  const eventPayload = {
    event: eventName,
    security_storage: "granted",
    user_id: userContext.user_id,
    user_uid: userContext.user_uid,
    visitor_id: resolvedVisitorId,
  };

  // For consent-banner interaction events (manage_cookies, reject, accept, etc.)
  // we need to temporarily grant analytics_storage so the GA4 tag's built-in
  // consent check allows the hit through as a full event (not a cookieless ping).
  // We then immediately revert to the real consent state.
  const isConsentInteraction = CONSENT_INTERACTION_EVENTS.has(eventName);
  const needsTemporaryGrant =
    isConsentInteraction && consentUpdate.analytics_storage === "denied";

  if (needsTemporaryGrant) {
    gtag("consent", "update", {
      ...consentUpdate,
      analytics_storage: "granted",
    });

    gtmPush({
      ...eventPayload,
      ...consentUpdate,
      analytics_storage: "granted",
    });

    gtag("consent", "update", consentUpdate);
  } else {
    gtag("consent", "update", consentUpdate);

    gtmPush({
      ...eventPayload,
      ...consentUpdate,
    });
  }
};

// Serialize the generic `eventParams` object into a SINGLE GA4/BigQuery
// parameter (`event_details`) as a JSON string. This keeps GTM/GA4 config to a
// one-time setup: one Data Layer Variable + one custom dimension named
// `event_details`, regardless of how many keys or events we add later. The
// param KEY (`event_details`) is fixed; the VALUE stays fully dynamic.
// In BigQuery, read individual fields with JSON_EXTRACT_SCALAR on the param.
const buildEventDetails = (eventParams) => {
  if (!eventParams || typeof eventParams !== "object") return {};
  const keys = Object.keys(eventParams);
  if (keys.length === 0) return {};
  try {
    return { event_details: JSON.stringify(eventParams) };
  } catch {
    return {};
  }
};

// ---------------------------------------------------------------------------
// dataLayer param-bleed protection
// ---------------------------------------------------------------------------
// GTM merges dataLayer state across pushes: a value set by one event lingers on
// every later event that never sets it (e.g. `bid_id`/`listing_id`/`seller_id`
// from a Place Bid event bleeding onto a later Favourite Saved event).
//
// Rather than hand-maintain a list of "params that might bleed" (fragile — easy
// to forget when adding a new param), we take the opposite approach:
//
//   1. EVENT_SCOPED_PARAMS is the single source of truth for every param that a
//      tracker can attach to an individual event (i.e. anything that is NOT
//      always-present context).
//   2. On every push we FIRST reset all of them to `undefined`, THEN spread the
//      event's actual params (which re-set only the ones this event provides).
//
// Result: no event-scoped param can carry over, and adding a new one only
// requires listing it here once. Always-present context (user/page/consent)
// lives in getUserContext()/getPageInfo() and is intentionally NOT reset.
export const EVENT_SCOPED_PARAMS = [
  "event_category",
  "event_label",
  "event_source",
  "event_details",
  "bid_id",
  "listing_id",
  "property_id",
  "property_type",
  "seller_id",
  "conversion",
  "external",
  "isBrokerAffiliate",
];

const buildResetParams = () =>
  EVENT_SCOPED_PARAMS.reduce((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});

// Enhanced trackGTMEvent with automatic context and consent check
export const trackGTMEvent = ({ action, params = {} }) => {
  if (!hasAnalyticsConsent()) return;

  const userContext = getUserContext();
  const pageInfo = getPageInfo();

  gtmPush({
    event: action,
    ...userContext,
    ...pageInfo,
    // Clear all event-scoped params first so nothing bleeds from a prior event,
    // then apply this event's actual params (which re-set the ones it provides).
    ...buildResetParams(),
    ...params,
  });
};

export const trackEvent = ({
  name = "Unspecified Event",
  category,
  conversion = false,
  external = true,
  // Emit `event_source` whenever an eventSource is provided. Pass
  // `eventSourceRequired: false` explicitly at a call site to opt out.
  eventSourceRequired = true,
  eventLabel,
  eventSource,
  bidId,
  listingId,
  property_id,
  user_uid,
  property_type,
  seller_id,
  // Generic passthrough for additional detail (e.g. section, payment_type,
  // enabled, trigger_type, error_type). Serialized into a single `event_details`
  // JSON param (see buildEventDetails) so GTM/GA4 needs only ONE variable +
  // custom dimension. Keeps event names short (<=40 chars) while preserving the
  // detail that used to live in the name.
  eventParams = {},
}) => {
  if (!hasAnalyticsConsent()) return; // Stop if no consent

  const userContext = getUserContext();
  const params = {
    event_category: category,
    event_label: eventLabel,
    ...(eventSource && eventSourceRequired
      ? { event_source: eventSource }
      : {}),
    ...(bidId ? { bid_id: bidId } : {}),
    ...(listingId ? { listing_id: listingId } : {}),
    ...(property_id ? { property_id } : {}),
    ...(conversion ? { conversion: true } : {}),
    ...(external ? { external: true } : {}),
    ...(property_type ? { property_type } : {}),
    ...(seller_id ? { seller_id } : {}),
    ...buildEventDetails(eventParams),
    user_id: user_uid || userContext.user_id,
    user_uid: user_uid || userContext.user_uid,
  };

  trackGTMEvent({ action: name, params });
};

// Add this function to track page views
export const trackPageView = (path) => {
  if (!hasAnalyticsConsent()) return; // Stop if no consent

  gtmPush({
    event: "hubzu_page_view",
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
};

// Track API calls
export const trackApiCall = ({
  pageName,
  eventName,
  eventCategory,
  endpoint,
  status,
  eventLabel,
  eventSource,
  bidId,
  listingId,
  property_id,
  // Emit `event_source` whenever an eventSource is provided. Pass
  // `eventSourceRequired: false` explicitly at a call site to opt out.
  eventSourceRequired = true,
  user_uid,
  isBrokerAffiliate,
  eventParams = {},
}) => {
  if (!hasAnalyticsConsent()) return;
  const userContext = getUserContext();
  const params = {
    event_category: eventCategory,
    event_label: eventLabel,
    ...(eventSource && eventSourceRequired
      ? {
          event_source:
            endpoint && status
              ? `${eventSource} ${endpoint} - Status: ${status}`
              : eventSource,
        }
      : {}),
    ...(bidId ? { bid_id: bidId } : {}),
    ...(listingId ? { listing_id: listingId } : {}),
    ...(property_id ? { property_id } : {}),
    ...(isBrokerAffiliate ? { isBrokerAffiliate } : {}),
    ...buildEventDetails(eventParams),
    user_id: user_uid || userContext.user_id,
    user_uid: user_uid || userContext.user_uid,
  };

  trackGTMEvent({ action: pageName ?? eventName, params });
};

// Track button clicks
// Accepts either `pageName` or `eventName` for the GA4 event name — use
// whichever fits the call site. If both are passed, `eventName` wins.
export const trackButtonClick = ({
  pageName,
  eventName,
  eventCategory,
  eventLabel,
  eventSource,
  bidId,
  listingId,
  property_id,
  eventSourceRequired,
  user_uid,
  eventParams = {},
}) => {
  trackEvent({
    name: eventName ?? pageName,
    category: eventCategory,
    eventLabel,
    eventSource,
    bidId,
    listingId,
    property_id,
    eventSourceRequired,
    user_uid,
    eventParams,
  });
};

// Track page mounts
export const trackPageMount = ({
  pageName,
  eventName,
  eventCategory,
  eventLabel,
  eventSource,
  bidId,
  listingId,
  property_id,
  eventSourceRequired,
  user_uid,
  seller_id,
  property_type,
  eventParams = {},
}) => {
  trackEvent({
    name: pageName ?? eventName,
    category: eventCategory,
    eventLabel,
    eventSource,
    bidId,
    listingId,
    property_id,
    eventSourceRequired,
    user_uid,
    eventParams,
    ...(seller_id ? { seller_id } : {}),
    ...(property_type ? { property_type } : {}),
  });
};
